import { supabase } from "@/lib/supabase";
import {
  SAMPLE_DISTRICT_NAME,
  SAMPLE_DISTRICT_SCHOOLS,
  SAMPLE_SCHOOL_TEAM_SNAPSHOT,
  type DistrictSchoolSummary,
  type FocusArea,
  type Observation,
  type SchoolTeamSnapshot,
} from "@/config/teamsSample";
import { emptySchoolTeamSnapshot } from "@/services/schoolTeamAggregation";

/**
 * `teamsService` — reads aggregated school/district data for the My Teams area.
 *
 * Read order for a real school:
 *   1. Persisted `school_team_snapshots` (+ focus/observation child rows) when populated
 *   2. Live aggregation via `school-team-snapshot` edge function (staff session activity)
 *   3. Empty snapshot for that school (never fake Lincoln Elementary data for a real UUID)
 *
 * Sample seed is only used when `schoolId` is missing or explicitly a design-review id.
 */

const SCHOOL_TEAM_SNAPSHOTS_TABLE = "school_team_snapshots" as const;
const SCHOOL_TEAM_FOCUS_AREAS_TABLE = "school_team_focus_areas" as const;
const SCHOOL_TEAM_OBSERVATIONS_TABLE = "school_team_observations" as const;

interface SnapshotRow {
  id: string;
  school_id: string;
  school_name: string | null;
  tagline: string | null;
  semester_label: string | null;
  helper_count: number | null;
}

interface FocusRow {
  id: string;
  snapshot_id: string;
  chat_entry: string | null;
  label: string;
  category: string;
  accent: FocusArea["accent"];
  percent: number;
  sort_order: number;
}

interface ObservationRow {
  id: string;
  snapshot_id: string;
  direction: Observation["direction"];
  body: string;
  tags: { label: string; tone: Observation["tags"][number]["tone"] }[] | null;
  sort_order: number;
}

function snapshotToDistrictSchoolSummary(
  row: { id: string; name: string },
  snap: SchoolTeamSnapshot,
): DistrictSchoolSummary {
  const focusAreaCount = snap.focusAreas.length;
  return {
    schoolId: row.id,
    schoolName: row.name,
    semesterLabel: snap.semesterLabel,
    helperCount: snap.helperCount,
    headlineNote: snap.observations[0]?.body,
    focusAreaCount,
    hasAggregateSignal: snap.helperCount > 0 && focusAreaCount > 0,
  };
}

function snapshotToSampleDistrictSummary(
  school: DistrictSchoolSummary,
  snap: SchoolTeamSnapshot,
): DistrictSchoolSummary {
  const focusAreaCount = snap.focusAreas.length;
  return {
    ...school,
    semesterLabel: snap.semesterLabel,
    helperCount: snap.helperCount,
    headlineNote: snap.observations[0]?.body,
    focusAreaCount,
    hasAggregateSignal: snap.helperCount > 0 && focusAreaCount > 0,
  };
}

function isSampleSchoolId(schoolId: string | null | undefined): boolean {
  return !schoolId || schoolId.startsWith("sample-");
}

async function fetchPersistedSnapshot(schoolId: string): Promise<SchoolTeamSnapshot | null> {
  const { data: snapshot, error: snapshotErr } = await supabase
    .from(SCHOOL_TEAM_SNAPSHOTS_TABLE as never)
    .select("id, school_id, school_name, tagline, semester_label, helper_count")
    .eq("school_id", schoolId)
    .order("generated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (snapshotErr || !snapshot) return null;

  const snapRow = snapshot as SnapshotRow;
  if ((snapRow.helper_count ?? 0) === 0) return null;

  const [focusResult, observationResult] = await Promise.all([
    supabase
      .from(SCHOOL_TEAM_FOCUS_AREAS_TABLE as never)
      .select("id, snapshot_id, chat_entry, label, category, accent, percent, sort_order")
      .eq("snapshot_id", snapRow.id)
      .order("sort_order", { ascending: true }),
    supabase
      .from(SCHOOL_TEAM_OBSERVATIONS_TABLE as never)
      .select("id, snapshot_id, direction, body, tags, sort_order")
      .eq("snapshot_id", snapRow.id)
      .order("sort_order", { ascending: true }),
  ]);

  const focusRows = (focusResult.data ?? []) as FocusRow[];
  const obsRows = (observationResult.data ?? []) as ObservationRow[];

  return {
    schoolId: snapRow.school_id,
    schoolName: snapRow.school_name ?? "Your school",
    tagline: snapRow.tagline ?? SAMPLE_SCHOOL_TEAM_SNAPSHOT.tagline,
    semesterLabel: snapRow.semester_label ?? "This semester",
    helperCount: snapRow.helper_count ?? 0,
    focusAreas: focusRows.map((row) => ({
      id: row.id,
      chatEntry: row.chat_entry as FocusArea["chatEntry"],
      label: row.label,
      category: row.category,
      accent: row.accent,
      percent: row.percent,
    })),
    observations: obsRows.map((row) => ({
      id: row.id,
      direction: row.direction,
      body: row.body,
      tags: (row.tags ?? []).map((t) => ({ label: t.label, tone: t.tone })),
    })),
  };
}

async function fetchLiveSchoolTeamSnapshot(schoolId: string): Promise<SchoolTeamSnapshot | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return null;

  const { data, error } = await supabase.functions.invoke("school-team-snapshot", {
    body: { school_id: schoolId },
    headers: { Authorization: `Bearer ${token}` },
  });

  if (error) {
    console.warn("[teamsService] live snapshot invoke failed:", error.message);
    return null;
  }

  const payload = data as { ok?: boolean; snapshot?: SchoolTeamSnapshot; error?: string } | null;
  if (payload?.error) {
    console.warn("[teamsService] live snapshot error:", payload.error);
    return null;
  }
  if (!payload?.ok || !payload.snapshot) return null;
  return payload.snapshot;
}

async function resolveSchoolName(schoolId: string): Promise<string> {
  const { data } = await supabase
    .from("schools" as never)
    .select("name")
    .eq("id", schoolId)
    .maybeSingle();
  return ((data as { name?: string } | null)?.name) ?? "Your school";
}

export async function getSchoolTeamSnapshot(schoolId: string | null | undefined): Promise<SchoolTeamSnapshot> {
  if (isSampleSchoolId(schoolId)) {
    return SAMPLE_SCHOOL_TEAM_SNAPSHOT;
  }

  try {
    const persisted = await fetchPersistedSnapshot(schoolId!);
    if (persisted) return persisted;

    const live = await fetchLiveSchoolTeamSnapshot(schoolId!);
    if (live) return live;

    const schoolName = await resolveSchoolName(schoolId!);
    return emptySchoolTeamSnapshot(schoolId!, schoolName);
  } catch (err) {
    console.warn("[teamsService] snapshot fetch threw — using empty snapshot.", err);
    const schoolName = await resolveSchoolName(schoolId!).catch(() => "Your school");
    return emptySchoolTeamSnapshot(schoolId!, schoolName);
  }
}

export interface DistrictRoster {
  districtName: string;
  schools: DistrictSchoolSummary[];
}

export async function canViewSchoolTeamInsights(
  schoolId: string,
  scope: { kind: "district"; districtId: string } | { kind: "school"; schoolId: string } | { kind: "super_admin" } | { kind: "none" },
): Promise<boolean> {
  if (isSampleSchoolId(schoolId)) return true;
  if (scope.kind === "super_admin") return true;
  if (scope.kind === "school") return scope.schoolId === schoolId;
  if (scope.kind === "district") {
    const { data } = await supabase
      .from("schools" as never)
      .select("district_id")
      .eq("id", schoolId)
      .maybeSingle();
    return (data as { district_id?: string } | null)?.district_id === scope.districtId;
  }
  return false;
}

export async function getDistrictRoster(districtId: string | null | undefined): Promise<DistrictRoster> {
  if (!districtId || districtId.startsWith("sample-")) {
    const schools = await Promise.all(
      SAMPLE_DISTRICT_SCHOOLS.map(async (school) => {
        const snap = await getSchoolTeamSnapshot(school.schoolId);
        return snapshotToSampleDistrictSummary(school, snap);
      }),
    );
    return { districtName: SAMPLE_DISTRICT_NAME, schools };
  }

  try {
    const { data: districtRow } = await supabase
      .from("districts" as never)
      .select("id, name")
      .eq("id", districtId)
      .maybeSingle();

    const { data: schoolsRows } = await supabase
      .from("schools" as never)
      .select("id, name")
      .eq("district_id", districtId)
      .order("name", { ascending: true });

    const districtName =
      ((districtRow as { name?: string } | null)?.name) ?? SAMPLE_DISTRICT_NAME;
    const rows = (schoolsRows ?? []) as { id: string; name: string }[];

    if (!rows.length) {
      return { districtName, schools: SAMPLE_DISTRICT_SCHOOLS };
    }

    const schools: DistrictSchoolSummary[] = await Promise.all(
      rows.map(async (row) => {
        const snap = await getSchoolTeamSnapshot(row.id);
        return snapshotToDistrictSchoolSummary(row, snap);
      }),
    );

    return { districtName, schools };
  } catch (err) {
    console.warn("[teamsService] district roster fetch failed — using sample.", err);
    return { districtName: SAMPLE_DISTRICT_NAME, schools: SAMPLE_DISTRICT_SCHOOLS };
  }
}
